import * as React from "react";

import { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";

import clsx from "clsx";
import { Theme } from "react-migration/lib/theme/Theme";

enum DisabledButtonBackgroundColor {
  Light = "disabled:bg-neutral-300!",
  Dark = "disabled:bg-neutral-700!",
}

enum DisabledElementTextContentColorOverride {
  Light = "disabled:text-text-base-primary-disabled!",
  Dark = "disabled:text-text-base-primary-disabled!",
}

enum DisabledElementBorderColorOverride {
  Light = "disabled:border-border-button-disabled!",
  Dark = "disabled:border-border-action-disabled!",
}

export type ButtonSize = "small" | "medium" | "large" | undefined;

type BaseProps = {
  size?: ButtonSize;
  theme?: Theme;
};

export type ButtonProps =
  | ({ buttonType: "Anchor" } & BaseProps & AnchorHTMLAttributes<HTMLAnchorElement>)
  | ({ buttonType?: "Button" } & BaseProps & ButtonHTMLAttributes<HTMLButtonElement>);

const disabledClassnames: Record<Theme, string> = {
  [Theme.Light]: `disabled:cursor-not-allowed ${DisabledButtonBackgroundColor[Theme.Light]} ${
    DisabledElementTextContentColorOverride[Theme.Light]
  } ${DisabledElementBorderColorOverride[Theme.Light]} disabled:shadow-none! border!`,
  [Theme.Dark]: `disabled:cursor-not-allowed ${DisabledButtonBackgroundColor[Theme.Dark]} ${
    DisabledElementTextContentColorOverride[Theme.Dark]
  } ${DisabledElementBorderColorOverride[Theme.Dark]} disabled:shadow-none! border!`,
};

const sharedClassnames =
  "inline-flex items-center justify-center font-semibold rounded-sm shadow-xs cursor-pointer no-underline hover:no-underline self-center";

const primaryClassNames =
  "bg-background-button-primary hover:bg-background-button-primary-hover border-none text-white hover:text-white focus:shadow-[0_0_0-4px] focus:shadow-positive-500/25";
const dangerClassNames =
  "bg-negative-500 hover:bg-negative-700 border-none text-white hover:text-white focus:shadow-[0_0_0-4px] focus:shadow-negative-500/25";
const secondaryClassNames =
  "bg-background-surface border border-solid border-neutral-300  hover:bg-background-surface-neutral-subtle active:bg-background-surface-neutral-subtle text-text-base-primary hover:text-text-base-primary shadow-secondary-button focus:shadow-[0_0_0-4px] focus:shadow-background-surface-neutral-subtle/25";
const borderlessClassNames: Record<Theme, string> = {
  [Theme.Light]: `bg-transparent disabled:bg-transparent! shadow-none! border-none text-text-base-active hover:text-text-base-active-hover disabled:!${
    DisabledElementTextContentColorOverride[Theme.Light]
  }`,
  [Theme.Dark]: `bg-transparent disabled:bg-transparent! shadow-none! border-none text-text-base-active hover:text-text-base-active-hover disabled:!${
    DisabledElementTextContentColorOverride[Theme.Dark]
  }`,
};

function Base({ size = "medium", theme = Theme.Light, className, ...props }: ButtonProps) {
  const classNames = clsx(
    sharedClassnames,
    disabledClassnames[theme],
    size === "small" && "px-3 h-8 text-xs leading-3!",
    size === "medium" && "px-4 h-9 text-sm leading-4!",
    size === "large" && "px-4 h-10 text-base leading-4!",
    className
  );

  if (props.buttonType === "Anchor") {
    const { buttonType: _, ...rest } = props;
    return <a type="button" {...rest} className={classNames} />;
  }

  const { buttonType: _, ...rest } = props;
  return <button {...rest} className={classNames} />;
}

const Primary: React.FC<ButtonProps> = ({ children, className, ...rest }) => (
  <Base className={clsx(primaryClassNames, className)} {...rest}>
    {children}
  </Base>
);

const Secondary: React.FC<ButtonProps> = ({ children, className, ...rest }) => (
  <Base className={clsx(secondaryClassNames, className)} {...rest}>
    {children}
  </Base>
);

const Danger: React.FC<ButtonProps> = ({ children, className, ...rest }) => (
  <Base className={clsx(dangerClassNames, className)} {...rest}>
    {children}
  </Base>
);

const Borderless: React.FC<ButtonProps> = ({ children, className, theme = Theme.Light, ...rest }) => (
  <Base className={clsx(borderlessClassNames[theme], className)} theme={theme} {...rest}>
    {children}
  </Base>
);

export const Button = {
  Primary,
  Secondary,
  Danger,
  Borderless,
  Base,
};

export default Button;
